import { create } from 'zustand';

export const TOAST_DURATION_MS = 6_000;
const MAX_TOASTS = 4;
const MIN_DURATION_MS = 1_000;

export interface Toast {
  readonly id: string;
  readonly title: string;
  readonly message: string;
  readonly type: 'info' | 'success' | 'warning' | 'error';
  readonly createdAt: number;
  readonly duration: number;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  readonly addToast: (toast: Omit<Toast, 'id' | 'createdAt' | 'duration'> & { duration?: number }) => void;
  readonly removeToast: (id: string) => void;
}

function triggerHaptic() {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  } catch {
    // Haptic not available
  }
}

// Timer registry — keyed by toast id so we can cancel on early dismiss
const timerRegistry = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = crypto.randomUUID();
    // Guard: clamp duration to a sensible minimum so progress bar is always visible
    const duration = Math.max(toast.duration ?? TOAST_DURATION_MS, MIN_DURATION_MS);
    triggerHaptic();
    set((state) => {
      const next = [...state.toasts, { ...toast, id, createdAt: Date.now(), duration }];
      // Cap at MAX_TOASTS — drop the oldest when over the limit
      return { toasts: next.slice(-MAX_TOASTS) };
    });
    // Store the timer so it can be cancelled if the user dismisses early
    const timer = setTimeout(() => {
      timerRegistry.delete(id);
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, duration);
    timerRegistry.set(id, timer);
  },
  removeToast: (id) => {
    // Cancel the auto-remove timer to avoid a redundant state update
    const timer = timerRegistry.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timerRegistry.delete(id);
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));
